import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";
import Tooltip from "../../Tooltip/Tooltip";
import { CommissionContract } from "../../../api/ComissionContractsAPI";
import SectionTitle from "../../SectionTitle/SectionTitle";
import InputShortText from "../../InputShortText/InputShortText";
import SelectInput from "../../SelectInput/SelectInput";
import TextOptionalActionButton from "../../TextOptionalActionButton/TextOptionalActionButton";
import { ButtonV2 } from "../../ButtonV2/ButtonV2";
import { PaymentStatus } from "../../../types/payment-statuses";
import { paymentStatusToTranslationKey } from "../../../utils/payment-status";
import {
  PaymentPlanItem,
  PaymentConcept,
  PAYMENT_CONCEPT_LABELS,
  PaymentStatus as PaymentPlanStatus,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
} from "../../../types/payment-plan";
import { CommissionPaymentPlan } from "../../../types/commissionPaymentPlan";

interface PaymentsTabSectionProps {
  form: CommissionContract;
  isEditing: boolean;
  paymentPlan?: CommissionPaymentPlan;
  onInputChange: (field: keyof CommissionContract) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onAddRequiredPayment: () => void;
  onPaymentPlanEdit: () => void;
  onDeletePaymentSplit: (index: number) => void;
}

export default function PaymentsTabSection({
  form,
  isEditing,
  paymentPlan,
  onInputChange,
  onAddRequiredPayment,
  onPaymentPlanEdit,
  onDeletePaymentSplit,
}: PaymentsTabSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <SectionTitle text={t("store_commission_panel.payments_title") || "Payments"} />
      <div className="mt-2 space-y-2">
        {isEditing ? (
          <SelectInput
            label={t("store_commission_panel.payment_status") || "Payment status"}
            values={Object.values(PaymentStatus).map((v) => ({
              value: v,
              readableValue: t(paymentStatusToTranslationKey(v)) || v,
            }))}
            defaultValue={form.payment}
            onChange={onInputChange("payment")}
          />
        ) : (
          <InputShortText
            label={t("store_commission_panel.payment_status") || "Payment status"}
            defaultValue={
              t(paymentStatusToTranslationKey(form.payment as PaymentStatus)) || form.payment
            }
            onChange={() => {}}
            isDisabled
          />
        )}
        <div className="flex items-center justify-between gap-2">
          <TextOptionalActionButton
            text={t("store_commission_panel.add_required_payment", "Add required payment")}
            onClick={onAddRequiredPayment}
            disabled={!isEditing}
            actionName="Add Required Payment"
            icon={<ExternalLink className="h-4 w-4" />}
            variant="blue"
          />
          {paymentPlan && paymentPlan.items.length > 0 && (
            <ButtonV2 variant="outline" size="sm" onClick={onPaymentPlanEdit} disabled={!isEditing}>
              {t("commission_payment_plan.edit") || "Edit"}
            </ButtonV2>
          )}
        </div>
        {paymentPlan && paymentPlan.items.length > 0 && (
          <div className="mt-2 space-y-2">
            {paymentPlan.items.map((item: PaymentPlanItem, index: number) => (
              <div key={index} className="border rounded p-3 bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="text-sm text-gray-700">
                    <div className="font-medium">
                      {t(PAYMENT_CONCEPT_LABELS[item.concept as PaymentConcept]) || item.concept}
                    </div>
                    <div className="text-gray-600">
                      {(item.amount || 0).toFixed(2)} {item.currency}
                    </div>
                    <div className="mt-1">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          PAYMENT_STATUS_COLORS[item.status as PaymentPlanStatus] === "green"
                            ? "bg-green-100 text-green-800"
                            : PAYMENT_STATUS_COLORS[item.status as PaymentPlanStatus] === "red"
                              ? "bg-red-100 text-red-800"
                              : PAYMENT_STATUS_COLORS[item.status as PaymentPlanStatus] === "yellow"
                                ? "bg-yellow-100 text-yellow-800"
                                : PAYMENT_STATUS_COLORS[item.status as PaymentPlanStatus] === "blue"
                                  ? "bg-blue-100 text-blue-800"
                                  : PAYMENT_STATUS_COLORS[item.status as PaymentPlanStatus] === "purple"
                                    ? "bg-purple-100 text-purple-800"
                                    : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {t(PAYMENT_STATUS_LABELS[item.status as PaymentPlanStatus]) || item.status}
                      </span>
                    </div>
                    {item.dueDate && (
                      <div className="text-gray-500">
                        {t("commission_payment_plan.due_date", "Due Date")}:{" "}
                        {new Date(item.dueDate).toLocaleDateString()}
                      </div>
                    )}
                    {item.validForDays != null && item.validForDays > 0 && (
                      <div className="text-gray-500">
                        {item.validForDays}{" "}
                        {item.validForDays === 1
                          ? t("global.day") || "day"
                          : t("store_commission_panel.commission_payment_plan.days") || "days"}
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <Tooltip content={t("store_commission_panel.remove_payment_split") || "Remove payment split"} position="bottom">
                      <button
                        type="button"
                        onClick={() => onDeletePaymentSplit(index)}
                        className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50 transition-colors"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
